import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SerializedSmartLink } from 'fym-common/src/db/serializersTypes';
import { SmartLinkWidgetType } from 'fym-common/src/db/enums';
import { widgetPictureUrl } from 'fym-shared/src/smartlinks/widgetPicture';
import { useSMLRouter } from '../../../routing/sml/hooks';
import { SMLRoutes } from '../../../routing/sml/SMLRoutes';
import { isSmartLinkDraft } from '../utils/isSmartLinkDraft';
import { usePublishPage } from './usePublishPage';

export interface PresaveChecklistItem {
  id: string;
  isDone: boolean;
  text: string;
  /** Only on the rows that are not done — a finished row has nothing to click. */
  actionLabel?: string;
  onAction?: () => void;
  isActionLoading?: boolean;
}

interface UsePresaveChecklistProps {
  smartLink: SerializedSmartLink;
}

/**
 * "Before release day", derived from the page itself rather than from a stored
 * to-do list. Every row is a fact we can already check, which is the point: the
 * old panel counted pre-saves on a page no fan could open and never said so.
 */
export function usePresaveChecklist({ smartLink }: UsePresaveChecklistProps): PresaveChecklistItem[] {
  const intl = useIntl();
  const router = useSMLRouter();
  const { publish, isLoading: isPublishing } = usePublishPage({
    accountId: smartLink.accountId,
    smartLinkId: smartLink.id,
  });

  const goToCreator = useCallback(() => {
    router.push(SMLRoutes.details, { accountId: smartLink.accountId, smartLinkId: smartLink.id });
  }, [router, smartLink.accountId, smartLink.id]);

  const hasCoverArt = useMemo(() => {
    if (smartLink.recentSmartLinkVersion.backgroundFile !== null) {
      return true;
    }
    // An Image block always counts. A Media block counts when it shows a picture or a video poster
    // (the Image block folded into the Media block on 2026-10-08).
    const widgets = smartLink.recentSmartLinkVersion.widgets ?? [];
    return widgets.some((widget) => widget.type === SmartLinkWidgetType.image || widgetPictureUrl(widget) !== null);
  }, [smartLink.recentSmartLinkVersion.backgroundFile, smartLink.recentSmartLinkVersion.widgets]);

  return useMemo(() => {
    const isDraft = isSmartLinkDraft(smartLink);
    const items: PresaveChecklistItem[] = [
      {
        id: 'releaseDate',
        isDone: true,
        text: intl.formatMessage({
          id: 'app.sml.presave.checklist.release_date',
          defaultMessage: 'Release date set',
        }),
      },
    ];

    if (isDraft) {
      items.push({
        id: 'draft',
        isDone: false,
        text: intl.formatMessage({
          id: 'app.sml.presave.checklist.draft',
          defaultMessage: "Page is still a draft — fans can't reach it",
        }),
        actionLabel: intl.formatMessage({ id: 'app.sml.presave.checklist.publish', defaultMessage: 'Publish' }),
        onAction: publish,
        isActionLoading: isPublishing,
      });
    }

    if (!hasCoverArt) {
      items.push({
        id: 'coverArt',
        isDone: false,
        text: intl.formatMessage({
          id: 'app.sml.presave.checklist.cover_art',
          defaultMessage: 'No cover art on the page',
        }),
        actionLabel: intl.formatMessage({ id: 'app.sml.presave.checklist.add', defaultMessage: 'Add' }),
        onAction: goToCreator,
      });
    }

    return items;
  }, [goToCreator, hasCoverArt, intl, isPublishing, publish, smartLink]);
}
